/**
 * Shared vocabulary for the LLM Usage surfaces (ADR-183 / ADR-188): a span's
 * outcome badge and the rule for reading a root's cron identity. Both the page
 * and the span tree render these, so they live beside neither.
 */
import type { LlmUsageAttribution } from '../api/client'

const STATUS_STYLES: Record<string, string> = {
  ok: 'bg-emerald-50 text-emerald-700',
  running: 'bg-sky-50 text-sky-700',
  incomplete: 'bg-amber-100 text-amber-800',
  error: 'bg-red-100 text-red-800',
  timeout: 'bg-red-100 text-red-800',
  cancelled: 'bg-orange-100 text-orange-800',
}

/**
 * A span's own outcome. This is a different claim from the capture and
 * completion state a trace header reports, and the two must not be conflated:
 * a run can be complete and still have failed every tool call inside it.
 */
export function StatusBadge({ status }: { status?: string | null }): React.ReactNode {
  if (!status) return null
  const style = STATUS_STYLES[status] ?? 'bg-gray-100 text-gray-600'
  return (
    <span className={`shrink-0 rounded px-1.5 py-0.5 text-xs font-medium ${style}`}>
      {status}
    </span>
  )
}

/**
 * A cron is identified by source and id together; either alone identifies
 * nothing, because an id is only unique within its source. Returns the
 * `<source>:<id>` key the rollup groups by, or null when the pair is absent.
 */
export function cronIdentity(attribution?: LlmUsageAttribution | null): string | null {
  if (!attribution?.cron_id || !attribution.cron_source) return null
  return `${attribution.cron_source}:${attribution.cron_id}`
}

/** What to call this root's cron to an operator: its job name, else its key. */
export function cronDisplayName(attribution?: LlmUsageAttribution | null): string | null {
  const identity = cronIdentity(attribution)
  if (!identity) return null
  return attribution?.cron_label ?? identity
}
